import { formatDistanceToNow } from 'date-fns';
import { ExternalLink, Loader2, XCircle } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  useCancelUpworkEscalationMutation,
  useGetUpworkEscalationStatusQuery,
  useStartUpworkEscalationMutation,
} from '@/services/api/generated/upworkEscalationsService';

interface UpworkEscalationCardProps {
  taskId: string;
  matterId: string;
}

export const UpworkEscalationCard: React.FC<UpworkEscalationCardProps> = ({
  taskId,
  matterId,
}) => {
  const [startEscalation, { isLoading: isStarting }] =
    useStartUpworkEscalationMutation();
  const [cancelEscalation, { isLoading: isCancelling }] =
    useCancelUpworkEscalationMutation();
  const [optimisticEscalation, setOptimisticEscalation] = React.useState<{
    startedAt: string;
    workflowId: string;
    expiresAt: number;
  } | null>(null);
  const [clockTick, setClockTick] = React.useState(0);

  const { data: escalationStatus, refetch: refetchStatus } =
    useGetUpworkEscalationStatusQuery(
      { matterId, taskId },
      {
        pollingInterval: 5000,
      },
    );

  const isOptimisticActive =
    !!optimisticEscalation && Date.now() < optimisticEscalation.expiresAt;
  const hasActiveEscalation =
    !!escalationStatus?.has_active_upwork_escalation || isOptimisticActive;
  let activeStartedAt: string | null = null;
  if (escalationStatus?.active_upwork_escalation_started_at) {
    activeStartedAt = escalationStatus.active_upwork_escalation_started_at;
  } else if (isOptimisticActive && optimisticEscalation) {
    activeStartedAt = optimisticEscalation.startedAt;
  }

  const activeWorkflowId =
    escalationStatus?.workflow_id || optimisticEscalation?.workflowId;

  React.useEffect(() => {
    if (
      escalationStatus?.has_active_upwork_escalation &&
      optimisticEscalation
    ) {
      setOptimisticEscalation(null);
    }
  }, [escalationStatus?.has_active_upwork_escalation, optimisticEscalation]);

  React.useEffect(() => {
    let intervalId: number | null = null;

    if (hasActiveEscalation) {
      intervalId = window.setInterval(() => {
        setClockTick((current) => current + 1);
      }, 10000);
    }

    return () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [hasActiveEscalation]);

  React.useEffect(() => {
    if (
      optimisticEscalation &&
      Date.now() >= optimisticEscalation.expiresAt &&
      !escalationStatus?.has_active_upwork_escalation
    ) {
      setOptimisticEscalation(null);
    }
  }, [
    clockTick,
    escalationStatus?.has_active_upwork_escalation,
    optimisticEscalation,
  ]);

  let startedAtRelative: string | null = null;
  if (activeStartedAt) {
    try {
      startedAtRelative = `Started ${formatDistanceToNow(new Date(activeStartedAt), { addSuffix: true })}`;
    } catch {
      startedAtRelative = null;
    }
  }

  let buttonContent: React.ReactNode = 'Start';
  if (isStarting) {
    buttonContent = (
      <>
        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        Starting
      </>
    );
  } else if (hasActiveEscalation) {
    buttonContent = (
      <>
        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
        In Progress
      </>
    );
  }

  const handleStartEscalation = React.useCallback(async () => {
    try {
      await startEscalation({
        matterId,
        taskId,
        startUpworkEscalationRequest: {
          policy_key: 'medical_records_v1',
        },
      }).unwrap();
      const startedAt = new Date().toISOString();
      setOptimisticEscalation({
        startedAt,
        workflowId: `upwork-escalation-task-${taskId}`,
        expiresAt: Date.now() + 60_000,
      });
      refetchStatus();
      toast.success(
        'Upwork escalation started. Request posted to Upwork Slack.',
      );
    } catch (error: unknown) {
      const err = error as { data?: { error?: string; detail?: string } };
      const message =
        err?.data?.error ||
        err?.data?.detail ||
        'Failed to start Upwork escalation';
      toast.error(message);
    }
  }, [matterId, refetchStatus, startEscalation, taskId]);

  const handleCancelEscalation = React.useCallback(async () => {
    try {
      await cancelEscalation({
        matterId,
        taskId,
        cancelUpworkEscalationRequest: {},
      }).unwrap();
      setOptimisticEscalation(null);
      refetchStatus();
      toast.success('Upwork escalation cancelled. Slack thread updated.');
    } catch (error: unknown) {
      const err = error as { data?: { error?: string; detail?: string } };
      const message =
        err?.data?.error ||
        err?.data?.detail ||
        'Failed to cancel Upwork escalation';
      toast.error(message);
    }
  }, [cancelEscalation, matterId, refetchStatus, taskId]);

  return (
    <div className="rounded-lg border border-zinc-200 p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4 flex-shrink-0 text-orange-600" />
            <span className="text-sm font-medium text-zinc-900">
              Upwork Escalation
            </span>
          </div>
          <p className="text-xs font-medium text-gray-500">
            Post this records request to Upwork for manual follow-up.
          </p>
        </div>
        <Button
          onClick={handleStartEscalation}
          disabled={isStarting || hasActiveEscalation}
          size="sm"
          className="flex-shrink-0"
        >
          {buttonContent}
        </Button>
      </div>

      {hasActiveEscalation && (
        <div className="rounded-md border border-orange-200 bg-orange-50 px-2.5 py-2 space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-orange-700">
              Escalation in progress.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleCancelEscalation}
              disabled={isCancelling}
            >
              {isCancelling ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <XCircle className="h-3 w-3 mr-1" />
              )}
              {isCancelling ? 'Cancelling' : 'Cancel'}
            </Button>
          </div>
          {startedAtRelative && (
            <p className="text-xs font-medium text-gray-600">
              {startedAtRelative}
            </p>
          )}
          {activeWorkflowId && (
            <p className="text-xs font-medium text-gray-600">
              Workflow: {activeWorkflowId}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
